import * as Yup from 'yup';

import bankDetailsValidationShape from './bankDetails.validation';

const businessInformationValidationSchema = (postalLabel: string) => {
    return Yup.object().shape({
        // Business Information
        taCode: Yup.string().required('TA Code is Required'),
        memberTaName: Yup.string(),
        existingAcquirerRelationship: Yup.boolean(),
        merchantNumber: Yup.string().when(['existingAcquirerRelationship'], {
            is: (existingAcquirerRelationship) => {
                return existingAcquirerRelationship === true;
            },
            then: Yup.string().required('Enter a Merchant Number').nullable(),
        }),
        // Goods and Services
        goodsServicesOwned: Yup.boolean(),
        acceptFullPayment: Yup.boolean(),
        acceptDepositPayment: Yup.boolean(),
        percentagePaymentPriorToDelivery: Yup.number()
            .when(['acceptFullPayment'], {
                is: (acceptFullPayment) => {
                    return acceptFullPayment === true;
                },
                then: Yup.number().required('Enter Percentage of Payment Prior to Delivery'),
            })
            .min(1, 'A minimum of 1% is required')
            .max(100, 'Amount exceeds 100%'),
        percentageDepositTaken: Yup.number()
            .when(['acceptDepositPayment'], {
                is: (acceptDepositPayment) => {
                    return acceptDepositPayment === true;
                },
                then: Yup.number().required('Enter Percentage of Goods or Services').nullable(),
            })
            .min(1, 'A minimum of 1% is required')
            .max(100, 'Amount exceeds 100%'),
        sizeOfDepositTaken: Yup.number()
            .when(['acceptDepositPayment'], {
                is: (acceptDepositPayment) => {
                    return acceptDepositPayment === true;
                },
                then: Yup.number()
                    .required('Enter the size of the deposit paid in advance')
                    .nullable(),
            })
            .min(1, 'A minimum of 1% is required')
            .max(100, 'Amount exceeds 100%'),
        levyChargeForGuarantees: Yup.boolean(),
        percentageOfCardTurnover: Yup.number()
            .when(['levyChargeForGuarantees'], {
                is: (levyChargeForGuarantees) => {
                    return levyChargeForGuarantees === true;
                },
                then: Yup.number().required('Enter the Percentage of Card Turnover'),
            })
            .min(1, 'A minimum of 1% is required')
            .max(100, 'Amount exceeds 100%'),
        averageLengthOfGuarantees: Yup.number()
            .when(['levyChargeForGuarantees'], {
                is: (levyChargeForGuarantees) => {
                    return levyChargeForGuarantees;
                },
                then: Yup.number().required('Enter Average Length of Gurantees'),
            })
            .min(1, 'A mininum of 1 month is required')
            .max(12, 'Month exceeds 12 months'),
        percentageOfGoodsReturned: Yup.number()
            .when(['levyChargeForGuarantees'], {
                is: (levyChargeForGuarantees) => {
                    return levyChargeForGuarantees === true;
                },
                then: Yup.number().required('Enter Percentage of Goods Returned'),
            })
            .min(1, 'A minimum of 1% is required')
            .max(100, 'Amount exceeds 100%'),
        stocksHeldAnyAddress: Yup.boolean(),
        tradingAddress_addressLine1: Yup.string().when(['stocksHeldAnyAddress'], {
            is: (stocksHeldAnyAddress) => {
                return stocksHeldAnyAddress === true;
            },
            then: Yup.string().required('Enter Address Line 1').nullable(),
        }),
        tradingAddress_townOrCity: Yup.string().when(['stocksHeldAnyAddress'], {
            is: (stocksHeldAnyAddress) => {
                return stocksHeldAnyAddress === true;
            },
            then: Yup.string().required('Enter Town or City of Address').nullable(),
        }),
        tradingAddress_county: Yup.string().when(['stocksHeldAnyAddress'], {
            is: (stocksHeldAnyAddress) => {
                return stocksHeldAnyAddress === true;
            },
            then: Yup.string().required('Enter County').nullable(),
        }),
        stockEircode: Yup.string().when(['stocksHeldAnyAddress'], {
            is: (stocksHeldAnyAddress) => {
                return stocksHeldAnyAddress === true;
            },
            then: Yup.string().required(`Enter ${postalLabel}`).nullable(),
        }),
        averageTimeAdvancePaymentTaken: Yup.string().when('acceptDepositPayment', {
            is: true,
            then: Yup.string().required('Enter Average Time in Advance of Delivery of Goods'),
            otherwise: Yup.string().notRequired(),
        }),
        averageTimeFullPaymentTaken: Yup.string().when('acceptDepositPayment', {
            is: true,
            then: Yup.string().required('Enter Average Time in Advance of Full Payment'),
            otherwise: Yup.string().notRequired(),
        }),
        levyChargeForMembership: Yup.boolean(),
        percentageOfCardTurnoverForMembership: Yup.number()
            .when(['levyChargeForMembership'], {
                is: (levyChargeForMembership) => {
                    return levyChargeForMembership === true;
                },
                then: Yup.number()
                    .required('Enter Percentage of Card Turn Over for Membership')
                    .nullable(),
            })
            .min(1, 'A minimum of 1% is required')
            .max(100, 'Amount exceeds 100%'),
        averageLengthOfMembership: Yup.number()
            .when(['levyChargeForMembership'], {
                is: (levyChargeForMembership) => {
                    return levyChargeForMembership === true;
                },
                then: Yup.number().required('Enter Average Length of Membership Subscriptions'),
            })
            .min(1, 'A mininum of 1 month is required')
            .max(12, 'Month exceeds 12 months'),
        costOfMembership: Yup.string().when(['levyChargeForMembership'], {
            is: (levyChargeForMembership) => {
                return levyChargeForMembership === true;
            },
            then: Yup.string().required('Enter Cost of Membership'),
        }),

        averageTimeFullPaymentDeliveryTaken: Yup.string().when(['acceptFullPayment'], {
            is: (acceptFullPayment) => {
                return acceptFullPayment === true;
            },
            then: Yup.string().required(
                'Enter Average Time in Advance of Delivery of goods/services '
            ),
        }),
        guaranteesWarrantiesThirdParty: Yup.boolean(),
        thirdPartyProviderName: Yup.string().when(['guaranteesWarrantiesThirdParty'], {
            is: (guaranteesWarrantiesThirdParty) => {
                return guaranteesWarrantiesThirdParty === true;
            },
            then: Yup.string().required('Enter Third Party Provider Name'),
        }),

        // Card Turnover
        creditCardTurnover: Yup.number().required('Credit Card Turnover is required'),
        totalCardTurnover: Yup.number().required('Total Card Turnover is required'),
        averageCardTransaction: Yup.number().required('Average Card Transaction Value is required'),
        totalAnnualCompanyTurnover: Yup.number().required(
            'Total Annual Company Turnover is required'
        ),
        percentageOfCompanyTurnover: Yup.number()
            .required('Percentage of Company Turnover is required')
            .min(1, 'A minimum of 1% is required')
            .max(100, 'Amount exceeds 100%'),
        percentageViaCard: Yup.number()
            .required('Percentage Via Card is required')
            .min(1, 'A minimum of 1% is required')
            .max(100, 'Amount exceeds 100%'),
        // Bank Details
        settlementAccountSameAsChargeable: Yup.boolean(),
        ...bankDetailsValidationShape,
        // Stock Address
        tradingAddress_country: Yup.string(),
        tradingAddress_addressLine2: Yup.string(),
    });
};

export default businessInformationValidationSchema;
